import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AsyncSelect from 'react-select/async';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
import { SearchClearIndicator, SearchControl, SearchDropdownIndicator, SearchLoadingIndicator, SearchIndicatorsContainer } from './components';
import { debouncePromise } from './helpers/utils';
const propTypes = {
  /**
   * a unique ID that will be used for the
   * select field's `id` and the label's `htmlFor` value
   */
  id: PropTypes.string.isRequired,
  /**
   * the label for the select input
   */
  label: PropTypes.string.isRequired,
  /**
   * function that takes the search query string and returns a promise
   * that resolves with select options
   */
  loadOptions: PropTypes.func.isRequired,
  /**
   * Additional custom classes for the input container element
   */
  className: PropTypes.string,
  /**
   * Override the components for the react-select component
   */
  componentsOverride: PropTypes.object,
  /**
   * Default options to provide to the search input.
   * Set to `true` to immediately call the `loadOptions` function with an empty query,
   * or as an array with initial select options to display.
   */
  defaultOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * render the component's error state
   */
  hasError: PropTypes.bool,
  /**
   * accessibly hide the field's form label
   */
  hideLabel: PropTypes.bool,
  /**
   * disables the form field
   */
  isDisabled: PropTypes.bool,
  /**
   * enables the loading state
   */
  isLoading: PropTypes.bool,
  /**
   * sets the select field to required="true"
   */
  isRequired: PropTypes.bool,
  /**
   * Message to display while performing search function
   * Function accepts current search query and returns message string.
   */
  loadingMessage: PropTypes.func,
  /**
   * Text to display when there are no options.
   * Function accepts current search query and returns message string.
   */
  noOptionsMessage: PropTypes.func,
  /**
   * Function called when the value of the input changes.
   * The selected option object is passed as the first argument.
   */
  onChange: PropTypes.func,
  /**
   * Displayed in input before user types
   */
  placeholder: PropTypes.string,
  /**
   * Additional props for the react-select async component
   * https://react-select.com/props
   */
  reactSelectProps: PropTypes.object,
  /**
   * For use with controlled inputs, use this prop to set the current value.
   * Used with the `onChange` prop.
   */
  value: PropTypes.object
};
const defaultProps = {
  className: '',
  componentsOverride: {},
  defaultOptions: false,
  errorLevel: 'error',
  errorMessage: undefined,
  hasError: false,
  hideLabel: true,
  isDisabled: false,
  isLoading: false,
  isRequired: false,
  loadingMessage: () => 'Loading…',
  noOptionsMessage: () => 'No results found',
  placeholder: '',
  reactSelectProps: {}
};
function SearchSelect(props) {
  const {
    className,
    componentsOverride,
    defaultOptions,
    errorLevel,
    errorMessage,
    hasError,
    hideLabel,
    id,
    isDisabled,
    isLoading,
    isRequired,
    label,
    loadOptions,
    loadingMessage,
    noOptionsMessage,
    onChange,
    placeholder,
    reactSelectProps,
    value
  } = props;
  const wrapperClasses = classNames('asc-form-group', {
    'is-disabled': isDisabled,
    [`asc-form-input--${errorLevel}`]: hasError,
    [className]: Boolean(className)
  });
  const containerClasses = classNames('asc-search-select-container');
  const customComponents = {
    DropdownIndicator: SearchDropdownIndicator,
    ClearIndicator: SearchClearIndicator,
    Control: SearchControl,
    LoadingIndicator: SearchLoadingIndicator,
    IndicatorsContainer: SearchIndicatorsContainer,
    ...componentsOverride
  };
  const debouncedLoadOptions = useCallback(debouncePromise(loadOptions, 250), []);
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)]
  };
  const ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    hideLabel: hideLabel,
    inputId: id,
    isRequired: isRequired,
    label: label
  }), /*#__PURE__*/React.createElement(AsyncSelect, _extends({
    "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null,
    backspaceRemovesValue: true,
    className: containerClasses,
    classNamePrefix: "asc-select",
    closeMenuOnSelect: true,
    components: customComponents,
    escapeClearsValue: true,
    defaultOptions: defaultOptions,
    inputId: id,
    isClearable: true,
    isDisabled: isDisabled,
    isLoading: isLoading,
    loadOptions: debouncedLoadOptions,
    loadingMessage: loadingMessage,
    noOptionsMessage: noOptionsMessage,
    onChange: onChange,
    openMenuOnClick: false,
    openMenuOnFocus: false,
    placeholder: placeholder,
    required: isRequired,
    value: value
  }, reactSelectProps)), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }));
}
SearchSelect.propTypes = propTypes;
SearchSelect.defaultProps = defaultProps;
export default SearchSelect;